/**
 * What a Historic Encounter *is*, in this port, and the one piece of geometry
 * all eight share.
 *
 * `HISTORIC.DAT` is text and six bytes per encounter (docs/formats/historic-dat.md):
 * a title, a briefing, and for each of the two seats an ace id, an aircraft
 * code and the line you read before you take it. **It carries no geometry at
 * all** — no positions, no altitudes, no counts. The original's shell builds
 * the scenario object stream for the encounter from a table compiled into
 * `PS.EXE`, which is not in any shipped data file, so the aeroplanes below are
 * this port's reading of the file's own prose: *"Hawker above you"*, *"five
 * Albatros D.IIs"*, *"you start as your plane overtakes the fleeing British
 * pilot's Sopwith Camel"*. Each `flights` list is annotated with the sentence
 * it comes from, and every encounter's `portNotes` says so on the briefing card.
 *
 * **Coordinates are written German-home-is-`+z`**, because that is the way the
 * file reads: side 0 is always the German seat. The player's own frame is
 * always "home is `+z`, the enemy is `−z`" (`mission-world.ts`), so flying the
 * Allied seat mirrors the whole scene through `z = 0` and turns every heading
 * around. `mirrorForAllied` is that one operation, and it is an involution:
 * applying it twice is the identity, which is what the test asserts instead of
 * trusting the sign work.
 */

import type { AiSkill } from "../combat/skill.ts";
import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { Vec3 } from "../flight/math3d.ts";
import type { Nation } from "./mission-roster.ts";
import type { MissionEra, MissionRegion, MissionWeather } from "./mission-world.ts";

/**
 * Where a Drachen sits. The same 1 150 m `generators-strike.ts` uses, so a
 * historic balloon and a generated one are the same target at the same height —
 * and comfortably inside the Mission Builder manual's *"keep balloons at 5 000
 * ft or lower"*.
 */
export const BALLOON_ALTITUDE_M = 1150;

/** One aeroplane in the encounter, positioned in German-home coordinates. */
export interface HistoricFlight {
  readonly id: string;
  readonly nation: Nation;
  readonly aircraft: AircraftKey;
  readonly callsign: string;
  /** The tier this aeroplane flies at when the player is not in it. */
  readonly skill: AiSkill;
  /** `mission-roster.ts` ace id, when this aeroplane is a named ace's — drives the livery. */
  readonly aceId?: string;
  /** Set on exactly one flight per nation: the seat the file offers you. */
  readonly seat?: Nation;
  readonly x: number;
  readonly altitudeM: number;
  readonly z: number;
  /** Radians. `0` points from the German side toward the Allied one. */
  readonly headingRad: number;
}

/** A Drachen on the map. Only the three balloon encounters have any. */
export interface HistoricBalloon {
  readonly id: string;
  readonly nation: Nation;
  readonly label: string;
  readonly x: number;
  readonly altitudeM: number;
  readonly z: number;
}

/**
 * What winning looks like from one seat. The file states no objective — it
 * states a *situation* — so these are read off the encounter the way the
 * original's own debrief would have scored it: a duel is won by shooting the
 * other man down, a balloon hunt by burning the gasbags, a balloon guard by
 * driving off the aeroplanes that came for them.
 */
export type HistoricObjective =
  | { readonly kind: "destroyAircraft"; readonly label: string; readonly count: number }
  | { readonly kind: "burnBalloons"; readonly label: string }
  | { readonly kind: "guardBalloons"; readonly label: string; readonly count: number };

/**
 * The file's own byte for a seat the ace roster does not hold. `HISTORIC.DAT`
 * writes one of exactly two values there, and it writes *different* ones for
 * the two seats of a single encounter (Lt. Roth Goes Balloon Hunting is `101`
 * German and `100` Allied), which is the whole reason this is kept rather than
 * flattened into `undefined`: whatever the byte selects, it is selected **per
 * seat**, not per encounter.
 */
export type UnnamedPilotCode = 100 | 101;

/** The half of `HISTORIC.DAT`'s side record that survives into a mission. */
export interface HistoricSeat {
  /** The file's `aceId` byte when it names one of the 25; `undefined` for its 100/101 unnamed-pilot codes. */
  readonly aceIndex: number | undefined;
  /** The file's `aceId` byte when it is *not* one of the 25. Set on exactly the six seats where `aceIndex` is `undefined` and the seat is playable. */
  readonly unnamedPilot?: UnnamedPilotCode;
  /** The file's `aircraftCode` byte. The resolved key is on the seat's flight. */
  readonly aircraftCode: number;
  /** The file's own lines for this seat, verbatim. */
  readonly lines: readonly string[];
  readonly objective: HistoricObjective;
  /**
   * `false` only for the British seat of Immelmann's First Victory, where the
   * file writes `FF FF FF` and `PS.EXE` answers *"You cannot play the %b side
   * of this mission."*
   */
  readonly playable: boolean;
}

export interface HistoricEncounterData {
  /** `historic-1` … `historic-8`, in the file's own order, which is the select screen's. */
  readonly id: string;
  /** Verbatim from the file. */
  readonly title: string;
  /** Verbatim from the file, one entry per NUL-terminated line. */
  readonly briefing: readonly string[];
  /** The real date, for the briefing card. Port-side: the file carries no date. */
  readonly when: string;
  readonly era: MissionEra;
  readonly region: MissionRegion;
  readonly weather: MissionWeather;
  readonly flights: readonly HistoricFlight[];
  readonly balloons?: readonly HistoricBalloon[];
  /** Which side the balloons belong to; the other side is the one attacking them. */
  readonly balloonNation?: Nation;
  readonly german: HistoricSeat;
  readonly allied: HistoricSeat;
  /** Where this port had to invent something the file does not carry. */
  readonly portNotes: readonly string[];
}

/**
 * Turns a German-home position into the player's own frame. For a German
 * player it is the identity; for an Allied one it reflects through the front
 * and turns the aeroplane around.
 */
export function mirrorForAllied(position: Vec3, headingRad: number, player: Nation): { position: Vec3; headingRad: number } {
  if (player === "german") return { position, headingRad };
  return {
    position: { x: -position.x, y: position.y, z: -position.z },
    headingRad: headingRad + Math.PI,
  };
}

/**
 * A two-seater the encounter is *fought over* rather than fought by: the Douai
 * raiders Immelmann caught, and the F.E.2bs Naval 10 came to rescue. All of
 * them are Allied, none is a seat, and every one is making for its own lines —
 * heading `0` in this file's German-home frame — so the five facts that differ
 * are the arguments and the four that do not are here.
 */
export function quarry(
  id: string,
  aircraft: AircraftKey,
  callsign: string,
  skill: AiSkill,
  station: readonly [x: number, altitudeM: number, z: number],
): HistoricFlight {
  return { id, nation: "allied", aircraft, callsign, skill, x: station[0], altitudeM: station[1], z: station[2], headingRad: 0 };
}

/** How a wingman is stationed on his leader: metres right, up, and back. */
export interface HistoricWing {
  readonly id: string;
  readonly callsign: string;
  readonly skill: AiSkill;
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;
}

/**
 * A flight in formation on another: same nation, same aeroplane, same heading,
 * offset from its leader — which is what every second and third aeroplane in
 * these encounters is, and is why they are written this way rather than as a
 * fourth copy of the same object literal.
 *
 * A wingman is never the player's seat and never carries an ace's livery: both
 * belong to the man he is flying on.
 */
export function wingman(leader: HistoricFlight, wing: HistoricWing): HistoricFlight {
  return {
    ...leader,
    id: wing.id,
    callsign: wing.callsign,
    skill: wing.skill,
    aceId: undefined,
    seat: undefined,
    x: leader.x + wing.dx,
    altitudeM: leader.altitudeM + wing.dy,
    z: leader.z + wing.dz,
  };
}

export function positionOf(flight: HistoricFlight): Vec3 {
  return { x: flight.x, y: flight.altitudeM, z: flight.z };
}

export function balloonPositionOf(balloon: HistoricBalloon): Vec3 {
  return { x: balloon.x, y: balloon.altitudeM, z: balloon.z };
}

/** The seat the file offers for a nation, or nothing when the encounter has none. */
export function seatFlightOf(data: HistoricEncounterData, nation: Nation): HistoricFlight | undefined {
  return data.flights.find((flight) => flight.seat === nation);
}

export function seatOf(data: HistoricEncounterData, nation: Nation): HistoricSeat {
  return nation === "german" ? data.german : data.allied;
}
